<template>
  <div class="p-4 sm:p-6">
    <div v-if="!selectedBoard" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Select board *</label>
        <multiselect
          v-model="selectedBoard"
          :options="availableBoards"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :searchable="true"
          :loading="boardsLoading"
          placeholder="Choose a board..."
          label="name"
          track-by="id"
          class="w-full"
        >
          <template #noResult>
            <span class="text-sm text-gray-500">No boards found.</span>
          </template>
        </multiselect>
        <p v-if="availableBoards.length === 0 && !boardsLoading" class="text-sm text-amber-600 mt-2">
          You have no boards yet. Create a board first from the Boards view.
        </p>
      </div>
      <div class="flex justify-end pt-4">
        <button type="button" @click="$emit('close')"
          class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>

    <div v-else-if="boardDetailsLoading" class="py-12 text-center">
      <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500 mx-auto"></div>
      <p class="text-sm text-gray-500 mt-2">Loading board...</p>
    </div>

    <div v-else-if="selectedBoard && boardColumns.length > 0">
      <button type="button" @click="selectedBoard = null"
        class="text-sm text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-1">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Change board
      </button>
      <create-task-modal
        :board-id="parseInt(selectedBoard.id, 10)"
        :columns="boardColumns"
        :board-members="boardMembers"
        @close="handleClose"
        @created="handleCreated" />
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import CreateTaskModal from './CreateTaskModal.vue';

export default {
  name: 'create-task-modal-standalone',
  components: {
    CreateTaskModal,
    Multiselect,
  },
  emits: ['close', 'created', 'layout-step'],
  props: {
    canViewAllBoards: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedBoard: null,
      boardColumns: [],
      boardMembers: [],
      boardDetailsLoading: false,
    };
  },
  apollo: {
    taskBoardsForSelect: {
      query: gql`
        query GetTaskBoardsForTaskCreate($first: Int!, $page: Int!) {
          taskBoards(first: $first, page: $page) {
            error
            data {
              id
              name
            }
          }
        }
      `,
      variables() {
        return { first: 100, page: 1 };
      },
      update(data) {
        return data && data.taskBoards ? data.taskBoards : { error: true, data: [] };
      },
      skip() {
        return this.canViewAllBoards;
      },
      fetchPolicy: 'cache-and-network',
    },
    allTaskBoardsForSelect: {
      query: gql`
        query GetAllTaskBoardsForTaskCreate($first: Int!, $page: Int!) {
          allTaskBoards(first: $first, page: $page) {
            error
            data {
              id
              name
            }
          }
        }
      `,
      variables() {
        return { first: 100, page: 1 };
      },
      update(data) {
        return data && data.allTaskBoards ? data.allTaskBoards : { error: true, data: [] };
      },
      skip() {
        return !this.canViewAllBoards;
      },
      fetchPolicy: 'cache-and-network',
    },
  },
  computed: {
    availableBoards() {
      const payload = this.canViewAllBoards ? this.allTaskBoardsForSelect : this.taskBoardsForSelect;
      if (!payload || (payload.error === true)) return [];
      const arr = payload.data;
      return Array.isArray(arr) ? arr : [];
    },
    boardsLoading() {
      if (this.canViewAllBoards) {
        return this.$apollo.queries.allTaskBoardsForSelect && this.$apollo.queries.allTaskBoardsForSelect.loading;
      }
      return this.$apollo.queries.taskBoardsForSelect && this.$apollo.queries.taskBoardsForSelect.loading;
    },
  },
  mounted() {
    this.emitLayoutStep();
  },
  methods: {
    emitLayoutStep() {
      var step = 1;
      if (this.selectedBoard) {
        step = 2;
      }
      this.$emit('layout-step', step);
    },
    async onBoardSelected() {
      const boardId = this.selectedBoard && this.selectedBoard.id;
      if (!boardId) {
        this.boardColumns = [];
        this.boardMembers = [];
        return;
      }
      this.boardDetailsLoading = true;
      try {
        const [structureResult, membersResult] = await Promise.all([
          this.$apollo.query({
            query: gql`
              query GetBoardStructureForCreate($id: ID!) {
                taskBoardStructure(id: $id) {
                  error
                  data {
                    columns {
                      id
                      name
                      order
                    }
                  }
                }
              }
            `,
            variables: { id: boardId },
            fetchPolicy: 'network-only',
          }),
          this.$apollo.query({
            query: gql`
              query GetBoardMembersForCreate($board_id: ID!) {
                taskBoardMembers(board_id: $board_id) {
                  error
                  data {
                    members {
                      id
                      name
                      email
                    }
                  }
                }
              }
            `,
            variables: { board_id: boardId },
            fetchPolicy: 'network-only',
          }),
        ]);

        const structure = structureResult.data && structureResult.data.taskBoardStructure;
        const members = membersResult.data && membersResult.data.taskBoardMembers;

        if (structure && !structure.error && structure.data && structure.data.columns) {
          this.boardColumns = structure.data.columns;
        } else {
          this.boardColumns = [];
        }

        if (members && !members.error && members.data && members.data.members) {
          this.boardMembers = members.data.members;
        } else {
          this.boardMembers = [];
        }
      } catch (e) {
        console.error('Error fetching board details:', e);
        this.boardColumns = [];
        this.boardMembers = [];
      } finally {
        this.boardDetailsLoading = false;
      }
    },
    handleClose() {
      this.selectedBoard = null;
      this.boardColumns = [];
      this.boardMembers = [];
      this.$emit('close');
    },
    handleCreated() {
      this.selectedBoard = null;
      this.boardColumns = [];
      this.boardMembers = [];
      this.$emit('created');
    },
  },
  watch: {
    selectedBoard(val) {
      if (val && val.id) {
        this.onBoardSelected();
      } else {
        this.boardColumns = [];
        this.boardMembers = [];
      }
      this.emitLayoutStep();
    },
  },
};
</script>

<style scoped>
/* Dropdown above modal stacking context (base-modal is z-50) */
:deep(.multiselect__content-wrapper) {
  z-index: 100;
}
</style>
